import moment from 'moment';
import notificationService from './notifications';

class GigNotificationChecker {
  constructor() {
    this.notifiedGigs = new Set(); // Track which gigs we've already notified about
    this.lastDailyCheck = null; // Track last daily summary notification
    this.lastUpcomingCheck = null; // Track last upcoming gigs check
  }

  // Reset notified sets (call this when gigs are refreshed)
  reset() {
    // Don't reset everything - keep daily tracking to avoid spam
    // Only reset if user explicitly wants to (or on new session)
  }

  // Check if we should show notifications today (only once per day)
  shouldCheckToday() {
    const today = moment().startOf('day');
    if (!this.lastDailyCheck || !moment(this.lastDailyCheck).isSame(today, 'day')) {
      this.lastDailyCheck = today.toDate();
      return true;
    }
    return false;
  }

  // Check for upcoming gigs (consolidated - only once per day in the morning)
  checkUpcomingGigs(gigs) {
    if (!gigs || gigs.length === 0) return;
    
    // Only check once per day, and only in the morning (before 12 PM)
    const now = moment();
    if (now.hour() >= 12) return; // Don't check after noon
    
    const todayKey = `upcoming-${now.format('YYYY-MM-DD')}`;
    if (this.notifiedGigs.has(todayKey)) return;

    const tomorrow = moment().add(1, 'day').startOf('day');
    const upcomingGigs = gigs.filter(gig => {
      if (!gig.start_date) return false;
      const isCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
      if (isCanceled) return false;
      const gigStartDate = moment(gig.start_date).startOf('day');
      return gigStartDate.isSameOrAfter(tomorrow, 'day') && gigStartDate.isBefore(moment().add(3, 'days'), 'day');
    });

    // Only notify if there are upcoming gigs, and only once per day
    if (upcomingGigs.length > 0 && this.shouldCheckToday()) {
      if (upcomingGigs.length === 1) {
        notificationService.showGigReminder(upcomingGigs[0]);
      } else {
        notificationService.showUpcomingGigsSummary(upcomingGigs.length);
      }
      this.notifiedGigs.add(todayKey);
    }
  }

  // Check for payment reminders (consolidated into daily summary)
  checkPaymentReminders(gigs) {
    if (!gigs || gigs.length === 0) return;

    // Only check once per day
    if (!this.shouldCheckToday()) return;

    const now = moment().startOf('day');
    const oneWeekAgo = moment().subtract(7, 'days').startOf('day');
    const oneDayAgo = moment().subtract(1, 'day').startOf('day');

    const unpaidGigs = gigs.filter(gig => {
      if (!gig.end_date) return false;
      const isCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
      const isPaid = gig.paid === 1 || gig.paid === true;
      if (isCanceled || isPaid) return false;
      
      const gigEndDate = moment(gig.end_date).startOf('day');
      // Only include gigs that ended 1 day or 1 week ago (not every day in between)
      return gigEndDate.isSame(oneDayAgo, 'day') || gigEndDate.isSame(oneWeekAgo, 'day');
    });

    // Send consolidated notification if there are unpaid gigs
    if (unpaidGigs.length > 0) {
      const todayKey = `payment-${now.format('YYYY-MM-DD')}`;
      if (!this.notifiedGigs.has(todayKey)) {
        notificationService.showPaymentReminderSummary(unpaidGigs);
        this.notifiedGigs.add(todayKey);
      }
    }
  }

  // Main check function - call this periodically (but only shows once per day)
  check(gigs) {
    this.checkUpcomingGigs(gigs);
    this.checkPaymentReminders(gigs);
  }

  // Clean up old notification keys (for gigs that are now paid or very old)
  cleanup(gigs) {
    const paidGigIds = new Set(
      gigs
        .filter(g => g.paid === 1 || g.paid === true)
        .map(g => g.id)
    );

    // Remove paid gigs from notification tracking
    this.notifiedGigs.forEach(key => {
      if (key.startsWith('payment-') && !key.includes(moment().format('YYYY-MM-DD'))) {
        const gigIdMatch = key.match(/\d+$/);
        if (gigIdMatch) {
          const gigId = parseInt(gigIdMatch[0]);
          if (paidGigIds.has(gigId)) {
            this.notifiedGigs.delete(key);
          }
        }
      }
    });
  }
}

// Export singleton instance
const gigNotificationChecker = new GigNotificationChecker();
export default gigNotificationChecker;

